import { HttpClient } from '@angular/common/http';
import { ITableDataSource } from '@suvo/shared/table';
import { HydratedMixin } from '@suvo/shared/interface-utils';
import { lastValueFrom } from 'rxjs';
import { ITableOptions } from '../../../shared/interfaces/table/table-options.interface';

export class TableApiService<Item extends object> {
  constructor(
    protected httpClient: HttpClient,
    private url: string,
  ) {}

  createTableParams(options: ITableOptions): object {
    if (!options) {
      return {};
    }

    const tableOptions: any = {};

    ['filters', 'simpleFilters', 'sort', 'pagination', 'properties'].forEach((option) => {
      const value = options[option];
      if (value) {
        if (option === 'filters') {
          if (options[option]?.query?.rules?.length) {
            if (options[option]?._id) {
              tableOptions[option] = JSON.stringify({
                id: options[option]._id,
              });
            } else {
              tableOptions[option] = JSON.stringify(options[option]);
            }
          }
          return null;
        } else if (option == 'sort' && options[option]) {
          let sortToUse = JSON.parse(JSON.stringify(options[option]));

          if (sortToUse?.direction.toString() == 'desc') {
            sortToUse.direction = 'dsc';
          } else if (sortToUse?.direction.toString() == 'asc') {
            sortToUse.direction = 'asc';
          }

          tableOptions[option] = JSON.stringify(sortToUse);
        } else {
          tableOptions[option] = JSON.stringify(value);
        }
      }
    });

    if (options.search) {
      tableOptions.search = options.search;
    }
    return tableOptions;
  }

  getRequestUrl(suffix: string | string[] = ''): string {
    //  this.url ends with a trailing slash.
    return suffix
      ? typeof suffix === 'string'
        ? `${this.url}${suffix}/`
        : `${this.url}${suffix.join('/')}/`
      : this.url;
  }

  get(suffix?: string | string[], params?: any) {
    return lastValueFrom(
      this.httpClient.get<Item & HydratedMixin<{ context: 'client' }>>(this.getRequestUrl(suffix), {
        params,
      }),
    );
  }

  put(body: Partial<Item>, suffix: string | string[] = '') {
    return lastValueFrom(
      this.httpClient.put<Item & HydratedMixin<{ context: 'client' }>>(
        `${this.getRequestUrl(suffix)}`,
        body,
      ),
    );
  }

  patchOne(suffix: string | string[] = '', id: string, body: Partial<Item>) {
    return lastValueFrom(
      this.httpClient.patch<Item & HydratedMixin<{ context: 'client' }>>(
        `${this.getRequestUrl(suffix)}${id}`,
        body,
      ),
    );
  }

  postOne(suffix: string | string[] = '', body: Partial<Item>) {
    return lastValueFrom(
      this.httpClient.post<Item & HydratedMixin<{ context: 'client' }>>(
        this.getRequestUrl(suffix),
        body,
      ),
    );
  }

  async deleteOne(suffix: string | string[] = '', id: string) {
    await lastValueFrom(this.httpClient.delete(`${this.getRequestUrl(suffix)}${id}`));
  }

  getPaginated(suffix: string | string[] = '', options: ITableOptions) {
    return this.get(suffix, this.createTableParams(options)) as unknown as Promise<
      ITableDataSource<Item & HydratedMixin<{ context: 'client' }>>
    >;
  }

  getOne(suffix: string | string[] = '', id: string) {
    return lastValueFrom(
      this.httpClient.get<Item & HydratedMixin<{ context: 'client' }>>(
        `${this.getRequestUrl(suffix)}${id}`,
      ),
    );
  }
}
